import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import aranoxHomePlugin from '@aranox/plugin-home';
import {
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  ApiBlueprint,
  configApiRef,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { navModule } from './modules/nav';
import { shellModule } from './modules/shell';
import './design-system/aranox.css';
import { themeModule } from './modules/theme';
import { CyrionSignInPage } from './modules/auth/CyrionSignInPage';
import { AranoxSignInPage } from './modules/auth/AranoxSignInPage';
import { VitalisSignInPage } from './modules/auth/VitalisSignInPage';
import { CyriusSignInPage } from './modules/auth/CyriusSignInPage';

const keycloakAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({
  id: 'auth.keycloak',
});

const keycloakAuthApi = ApiBlueprint.make({
  name: 'keycloak',
  params: defineParams =>
    defineParams({
      api: keycloakAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'oidc',
            title: 'ARANOX',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
});


const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => {
      const hostname =
        typeof window !== 'undefined'
          ? window.location.hostname.toLowerCase()
          : '';

      const isAranox = hostname === 'portal.aranox.com.br';
      const isCyrion = hostname === 'cyrion.aranox.com.br';
      const isCyrius = hostname === 'cyrius.aranox.com.br';
      const isVitalis = hostname === 'vitalis.aranox.com.br';

      const provider = {
        id: 'aranox-keycloak-auth-provider',
        title: isAranox
          ? 'Portal ARANOX'
          : isCyrion
            ? 'Portal CYRION'
            : isCyrius
              ? 'Portal CYRIUS'
              : isVitalis
                ? 'Portal VITALIS'
                : 'Portal ARANOX',
        message: isAranox
          ? 'Entrar com sua conta corporativa ARANOX'
          : isCyrion
            ? 'Entrar com sua conta corporativa CYRION'
            : isCyrius
              ? 'Entrar com sua conta corporativa CYRIUS'
              : isVitalis
                ? 'Entrar com sua conta corporativa VITALIS'
                : 'Entrar com sua conta corporativa ARANOX',
        apiRef: keycloakAuthApiRef,
      };

      return isAranox ? (
        <AranoxSignInPage {...props} provider={provider} />
      ) : isCyrion ? (
        <CyrionSignInPage {...props} provider={provider} />
      ) : isCyrius ? (
        <CyriusSignInPage {...props} provider={provider} />
      ) : isVitalis ? (
        <VitalisSignInPage {...props} provider={provider} />
      ) : (
        <SignInPage {...props} provider={provider} />
      );
    },
  },
});

const authModule
     = createFrontendModule({
  pluginId: 'app',
  extensions: [keycloakAuthApi, signInPage],
});

export default createApp({
  features: [themeModule, shellModule, catalogPlugin, aranoxHomePlugin, navModule, authModule],
});
